import { FC, useRef, forwardRef, ChangeEvent, MutableRefObject, DragEvent } from 'react';
import { FiUploadCloud } from 'react-icons/fi';
import { ImageEditorSize } from '../types/enums';
import { ImageEditorPropTypes } from './types/propsType';
import styles from './ImageEditor.module.scss';

/**
 *
 * @param file argument should a uploaded file
 * @returns process input file to readable Blob format URL
 */
const processFileToBlob = (file: File) => URL.createObjectURL(file);

const ImageEditor: FC<ImageEditorPropTypes> = forwardRef<HTMLDivElement, ImageEditorPropTypes>(
  (props: ImageEditorPropTypes, ref) => {
    const {
      maxFileCount,
      onFileUpload,
      imageEditorSize = styles[ImageEditorSize.INHERIT_SIZE],
      uploadCTALabel,
      fileSizeLimitLabel,
      // dragCTALabel,
      // connectorLabel,
      ...restProps
    } = props;
    const inputFileRef = useRef() as MutableRefObject<HTMLInputElement>;
    const inputFileUploadClick = () => {
      inputFileRef.current.click();
    };
    // Filters FileList according to provided props.
    const filterFiles = (files: FileList | null) => {
      let listFile: Record<number, File> = {};
      if (files) {
        listFile = { 0: files[0] };
        const limit = maxFileCount ?? Object.keys(files).length;
        if (restProps?.multiple) {
          for (let i = 1; i < limit; i++) {
            if (!files[i]) {
              break;
            }
            listFile[i] = files[i];
          }
        }
      }
      return listFile;
    };
    const handleFileUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      onFileUpload?.({
        ...e,
        files: filterFiles(files),
      });
    };
    const handleDrag = function (e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleDrop = function (e: DragEvent) {
      handleDrag(e);
      onFileUpload?.({
        ...e,
        files: filterFiles(e.dataTransfer.files),
      });
    };

    const sizeMappings: Record<string, typeof styles> = {
      [ImageEditorSize.FIXED_SIZE]: styles[ImageEditorSize.FIXED_SIZE],
      [ImageEditorSize.FIXED_HEIGHT]: styles[ImageEditorSize.FIXED_HEIGHT],
      [ImageEditorSize.INHERIT_SIZE]: styles[ImageEditorSize.INHERIT_SIZE],
    } as unknown as Record<string, typeof styles>;

    const sizeClassName = sizeMappings[imageEditorSize];
    let disableClassName: string = '';
    if (props?.disabled) {
      disableClassName = styles['disabled'];
    }
    return (
      <section
        className={`${styles['image-upload-container']} ${disableClassName} ${sizeClassName}`}
        onClick={inputFileUploadClick}
        onKeyDown={(e) => e.key === 'Enter' && inputFileUploadClick()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onFocus={() => null}
        data-testid="image-editor-container-testid"
        role="presentation"
        ref={ref}
      >
        <FiUploadCloud className={styles['image-cta-label']}/>
        <article className={styles['image-desc-text']}>
          <span className={styles['image-desc-text-desktop']}>
            {/* <span>{dragCTALabel || 'Drag & Drop to Upload'}</span>
            <span>{connectorLabel || 'Or'}</span> */}
            <span className={styles['image-cta-label']}>{uploadCTALabel || 'Choose Image from Local Computer'}</span>
          </span>
          {fileSizeLimitLabel && <span  className={styles['image-limit-label']}>{fileSizeLimitLabel}</span>}
        </article>
        <input
          type="file"
          ref={inputFileRef}
          className={styles['input-image-upload']}
          onChange={handleFileUploadChange}
          data-testid="input-image-upload-testid"
          accept="image/*"
          {...restProps}
        />
      </section>
    );
  },
);
ImageEditor.displayName = 'ImageEditor';
export { processFileToBlob };
export default ImageEditor;
